import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { BaseService } from './base/base.service';
import { ListResponse, ObjectResponse, PagedResponse } from './../interfaces/response';

import {
  Entity,
  BackgroundInfoTopic,
  BackgroundInfoType,
  BackgroundInfoSubTopic,
  CopyBackgroundInfoPayload,
  EditSubTopicPayload,
  ReinitializePayload,
  CreateBackgroundInfoTemplate,
  BackgroundInfo,
  CopyBackgroundInfoOption,
  BackgroundInfoTemplate
} from '../models';
import { TranslationConstants } from 'src/app/shared/constants/translation';

import { BackgroundInfoTemplateEnum } from '../models/enums';
import { AppConfig } from './app-config';

@Injectable()
export class BackgroundInfoService extends BaseService<Entity> {
  public _currentSubTopicIndex = 0;

  private _subTopicIndexChanged = new Subject();
  // Needs debounceTime time pipe in order to wait for subscriber component to reinitialize
  public subTopicIndexChanged$ = this._subTopicIndexChanged.asObservable().pipe(debounceTime(100));
  private _getBackgroundInfo = new Subject();
  public getBackgroundInfo$ = this._getBackgroundInfo.asObservable();

  private _jurisdictionChanged = new Subject();
  public jurisdictionChanged$ = this._jurisdictionChanged.asObservable();

  constructor(http: HttpClient, protected config: AppConfig) {
    super(http, config);
  }

  public getBackgroundInfoData(resourceId: number, type: BackgroundInfoType): Observable<ListResponse<BackgroundInfo>> {
    return this.http.get<ListResponse<BackgroundInfo>>(`${this.getResourceUrl()}${resourceId}/topics/${type}`);
  }

  public getBackgroundInfoSubTopics(
    { name, country }: BackgroundInfoTopic,
    entityId: number,
    type: BackgroundInfoType
  ): Observable<ListResponse<BackgroundInfoSubTopic>> {
    const queryParams = `topic=${name}&${country ? `jurisdiction=${country.countryId}` : ''}`;
    const apiUrl = `${this.getResourceUrl()}${entityId}/subtopics/${type}?${queryParams}`;

    return this.http.get<ListResponse<BackgroundInfoSubTopic>>(apiUrl);
  }

  public getSubTopic(
    entityId: number,
    backgroundInfoId: number
  ): Observable<ListResponse<BackgroundInfoSubTopic>> {
    const queryParams = `backgroundInfoId=${backgroundInfoId}&`;
    const apiUrl = `${this.getResourceUrl()}${entityId}/subtopic?${queryParams}`;

    return this.http.get<ListResponse<BackgroundInfoSubTopic>>(apiUrl);
  }

  public getSourcesList(
    destinationObjectId: number,
    type: BackgroundInfoType,
    jurisdictionId?: number): Observable<ListResponse<CopyBackgroundInfoOption>> {
    let query = '';
    if (!!jurisdictionId) {
      query = `?jurisdiction=${jurisdictionId}`;
    }
    const apiUrl = `${this.getResourceUrl()}copy/${destinationObjectId}/type/${type}${query}`;

    return this.http.get<ListResponse<CopyBackgroundInfoOption>>(apiUrl);
  }

  public copyBackgroundInfo(
    destinationObjectId: number,
    type: BackgroundInfoType,
    payload: CopyBackgroundInfoPayload): Observable<ListResponse<BackgroundInfo>> {
    const apiUrl = `${this.getResourceUrl()}copy/${destinationObjectId}/type/${type}`;

    return this.http.put<ListResponse<BackgroundInfo>>(apiUrl, payload);
  }

  public getTranslationKeyByBackInfoType(type: BackgroundInfoType, isPlural: boolean = false): string {
    switch (type) {
      case BackgroundInfoType.Entity:
        return isPlural ? TranslationConstants.GLOBAL_ENTITIES : TranslationConstants.GLOBAL_ENTITY;
      case BackgroundInfoType.MNE:
        return isPlural ? TranslationConstants.GLOBAL_MNES : TranslationConstants.GLOBAL_MNE;
      case BackgroundInfoType.Transaction:
      case BackgroundInfoType.UncontrolledTransaction:
        return isPlural ? TranslationConstants.GLOBAL_TRANSACTIONS : TranslationConstants.GLOBAL_TRANSACTION;
      default:
        return '';
    }
  }

  public addNewSubtopic(
    objectId: number,
    useCase: number,
    data: BackgroundInfoSubTopic): Observable<ObjectResponse<BackgroundInfo>> {
    data.currentSubtopicIndex = this._currentSubTopicIndex;
    const apiUrl = `${this.getResourceUrl()}${objectId}/subtopics/${useCase}`;

    return this.http.post<ObjectResponse<BackgroundInfo>>(apiUrl, data);
  }

  public updateSubTopic(
    subTopic: BackgroundInfoSubTopic,
    resourceId: number,
    useCase: BackgroundInfoType
  ): Observable<ObjectResponse<BackgroundInfo>> {
    const payload = this.formatSubTopicPayload(subTopic);
    const apiUrl = subTopic.backgroundInfoId ?
      `${this.getResourceUrl()}${subTopic.backgroundInfoId}` :
      `${this.getResourceUrl()}${resourceId}/type/${useCase}`;

    return this.http.patch<ObjectResponse<BackgroundInfo>>(apiUrl, payload);
  }

  public initializeBackgroundInfo(objectId: number, useCase: number): Observable<ListResponse<BackgroundInfo>> {
    const apiUrl = `${this.getResourceUrl()}${objectId}/initialize/${useCase}`;

    return this.http.post<ListResponse<BackgroundInfo>>(apiUrl, {});
  }

  public reinitializeBackgroundInfo(
    objectId: number,
    useCase: number,
    data: ReinitializePayload): Observable<ListResponse<BackgroundInfo>> {
    const apiUrl = `${this.getResourceUrl()}${objectId}/reinitialize/${useCase}`;

    return this.http.post<ListResponse<BackgroundInfo>>(apiUrl, data);
  }

  public setCurrentSubtopicIndex(index: number): void {
    this._currentSubTopicIndex = index;
  }

  public increaseSubTopicIndex() {
    this._currentSubTopicIndex += 1;
  }

  public emitSubTopicIndexChanged(): void {
    this._subTopicIndexChanged.next(this._currentSubTopicIndex);
  }

  public getObjectCodeTranslationKey(useCase): string {
    return this.isTransactionType(useCase) ? 'transaction.transaction_id' : 'entity_financials.entity_code';
  }

  public getJurisdictionIsoCode(activeJurisdiction): string {
    return activeJurisdiction && activeJurisdiction.jurisdiction ? activeJurisdiction.jurisdiction.isoCode : 'global.all';
  }

  public getJurisdictionId(activeJurisdiction): number {
    return activeJurisdiction && activeJurisdiction.jurisdiction ? activeJurisdiction.jurisdiction.countryId : null;
  }

  public changeJurisdictionByIndex(index: number = 0): void {
    this._jurisdictionChanged.next(index);
  }

  public isTransactionType(useCase): boolean {
    return useCase === BackgroundInfoType.Transaction || useCase === BackgroundInfoType.UncontrolledTransaction;
  }

  // template related methods
  public createTemplate(
    resourceId: number,
    useCase: BackgroundInfoType,
    template: CreateBackgroundInfoTemplate
  ): Observable<ObjectResponse<BackgroundInfoTemplate>> {
    const objectType = this.mapBackgroundInfoUseCaseToTemplateUseCase(useCase);
    const apiUrl = `${this.getResourceUrl()}template/${resourceId}/type/${objectType}`;

    return this.http.post<ObjectResponse<BackgroundInfoTemplate>>(apiUrl, template);
  }

  public importTemplate(
    templateId: number,
    useCase: BackgroundInfoType,
    objectId: number
  ): Observable<ObjectResponse<BackgroundInfo>> {
    const type = this.mapBackgroundInfoUseCaseToTemplateUseCase(useCase);
    const apiUrl = `${this.getResourceUrl()}template/import/${templateId}`;

    return this.http.put<ObjectResponse<BackgroundInfo>>(apiUrl, { type, id: objectId });
  }

  public getBackgroundInfoTemplates(
    useCase: BackgroundInfoType,
    jurisdictionId: number,
    page: number,
    length: number
  ): Observable<PagedResponse<BackgroundInfoTemplate>> {
    const type = this.mapBackgroundInfoUseCaseToTemplateUseCase(useCase);
    const jurisdictionParam = jurisdictionId ? `&jurisdiction=${jurisdictionId}` : '';
    const queryParams = `type=${type}&page=${page}&length=${length}${jurisdictionParam}`;

    return this.http.get<PagedResponse<BackgroundInfoTemplate>>(`${this.getResourceUrl()}template?${queryParams}`);
  }

  public reFetchBackgroundInfo(jurisdiction: BackgroundInfo): void {
    this._getBackgroundInfo.next(jurisdiction);
  }

  protected getResourceUrl(): string {
    return `${this.baseUrl}background-info/`;
  }

  protected formatSubTopicPayload(subTopic: BackgroundInfoSubTopic): EditSubTopicPayload {
    const discussion = subTopic.discussion || '';
    const notes = subTopic.notes || '';
    const topicName = subTopic.topicName || '';
    const topicId = subTopic.topicId;
    const completed = subTopic.completed;

    const payload = {
      discussion,
      notes,
      topicId,
      completed
    } as EditSubTopicPayload;

    if (!subTopic.backgroundInfoId) {
      payload.topicName = topicName;
    }

    return payload;
  }

  private mapBackgroundInfoUseCaseToTemplateUseCase(useCase: BackgroundInfoType): number {
    switch (useCase) {
      case BackgroundInfoType.Entity:
        return BackgroundInfoTemplateEnum.Entity;
      case BackgroundInfoType.Transaction:
        return BackgroundInfoTemplateEnum.Transaction;
      case BackgroundInfoType.UncontrolledTransaction:
        return BackgroundInfoTemplateEnum.UncontrolledTransaction;
      default:
        break;
    }
  }
}
